import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Lightbulb, Globe, Shield, Users, Scale, Sparkles } from "lucide-react";

export const FeaturesSection = () => {
  const features = [
    {
      icon: Lightbulb,
      title: "AI Name Generation",
      description:
        "Generate powerful, memorable brand names using advanced AI algorithms trained on successful brands across industries.",
      benefits: [
        "Creative & unique suggestions",
        "Industry-specific names",
        "Memorable & brandable options",
      ],
    },
    {
      icon: Globe,
      title: "Domain Availability Checks",
      description:
        "Instantly check domain availability for your brand names and get scored recommendations for the best domain matches.",
      benefits: [
        "Real-time availability",
        "Multiple TLD options",
        "Domain scoring system",
      ],
    },
    {
      icon: Shield,
      title: "Trademark Risk Analysis",
      description:
        "Comprehensive trademark conflict analysis to inform you of potential legal risks before you commit to a brand name.",
      benefits: [
        "Conflict detection",
        "Risk assessment",
        "Link to Full Trademark Information",
      ],
    },
  ];

  const audiences = [
    {
      icon: Users,
      title: "Startup Founders",
      description:
        "Launch with confidence knowing your brand name is available, memorable, and legally sound.",
    },
    {
      icon: Sparkles,
      title: "Creative Professionals",
      description:
        "Establish online brand presence for your clients and projects with powerful AI tools that generate creative brand names, automatically check social media user names availability, and finds suitable domain names.",
    },
    {
      icon: Scale,
      title: "IP Attorneys",
      description:
        "Streamline trademark research and provide comprehensive brand name analysis to your clients.",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Features */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200 border-teal-200">
            Core Features
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Everything You Need for Brand Name Success
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our comprehensive platform combines AI-powered generation with legal
            analysis and domain research to give you complete brand name
            intelligence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className="border-teal-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white/80 backdrop-blur-sm"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {feature.description}
                  </p>

                  <div className="space-y-2">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <div
                        key={benefitIndex}
                        className="flex items-center space-x-2"
                      >
                        <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Target Audiences */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-cyan-100 text-cyan-700 hover:bg-cyan-200 border-cyan-200">
            Built For Professionals
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Trusted by Industry Leaders
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Name Pilot serves the unique needs of founders, creatives, and legal
            professionals who demand precision and reliability in brand naming.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {audiences.map((audience, index) => {
            const Icon = audience.icon;
            return (
              <Card
                key={index}
                className="border-cyan-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white/80 backdrop-blur-sm"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {audience.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {audience.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
